import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Typography,
    DatePicker,
    Select,
    Button,
    Table,
    Space,
    Progress,
    Divider,
} from 'antd';
import {
    UserOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
    DollarOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    BarChartOutlined,
    LineChartOutlined,
    PieChartOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import { formatCurrency } from '../../../utils/ui';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Reports = () => {
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);
    const [reportType, setReportType] = useState('overview');
    const [stats, setStats] = useState({});

    useEffect(() => {
        fetchReportData();
    }, [dateRange, reportType]);

    const fetchReportData = async () => {
        setLoading(true);
        try {
            const [usersRes, vendorsRes, bookingsRes] = await Promise.all([
                api.adminListUsers({ per_page: 1 }),
                api.adminListVendors({ per_page: 5 }),
                api.adminListBookings({ per_page: 5 })
            ]);

            const totalUsers = usersRes?.data?.total ?? usersRes?.data?.meta?.total ?? usersRes?.data?.data?.length ?? 0;
            const totalVendors = vendorsRes?.data?.total ?? vendorsRes?.data?.meta?.total ?? vendorsRes?.data?.data?.length ?? 0;
            const totalBookings = bookingsRes?.data?.total ?? bookingsRes?.data?.meta?.total ?? bookingsRes?.data?.data?.length ?? 0;

            setStats({
                totalUsers,
                totalVendors,
                totalBookings,
                totalRevenue: 0,
                monthlyGrowth: 0,
                conversionRate: 0,
                averageOrderValue: 0,
                repeatCustomers: 0,
                topVendors: (vendorsRes?.data?.data || []).map(v => ({ name: v.name || v.business_name || `Vendor #${v.id}`, bookings: v.bookings_count || 0, revenue: v.revenue || 0 })),
                recentBookings: (bookingsRes?.data?.data || []).map(b => ({ id: b.id, customer: b.customer_name || (b.user && b.user.name) || 'Customer', vendor: b.vendor_name || (b.vendor && b.vendor.name) || 'Vendor', amount: b.total_amount || 0, date: b.created_at }))
            });
        } catch (error) {
            console.error('Failed to fetch report data:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportReport = () => {
        // Implement report export functionality
        console.log('Exporting report...');
    };

    const bookingColumns = [
        {
            title: 'Customer',
            dataIndex: 'customer',
            key: 'customer',
        },
        {
            title: 'Vendor',
            dataIndex: 'vendor',
            key: 'vendor',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => formatCurrency(amount),
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('MMM DD, YYYY'),
        },
    ];

    const vendorColumns = [
        {
            title: 'Vendor',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Bookings',
            dataIndex: 'bookings',
            key: 'bookings',
        },
        {
            title: 'Revenue',
            dataIndex: 'revenue',
            key: 'revenue',
            render: (revenue) => formatCurrency(revenue),
        },
    ];

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Reports & Analytics</Title>
                <Text type="secondary">
                    Comprehensive business insights and performance metrics
                </Text>
            </div>

            {/* Filters */}
            <Card className="mb-6 card-compact">
                <Row gutter={16} align="middle">
                    <Col span={8}>
                        <Text strong>Date Range:</Text>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            style={{ width: '100%', marginTop: 8 }}
                        />
                    </Col>
                    <Col span={6}>
                        <Text strong>Report Type:</Text>
                        <Select
                            value={reportType}
                            onChange={setReportType}
                            style={{ width: '100%', marginTop: 8 }}
                        >
                            <Option value="overview">Overview</Option>
                            <Option value="financial">Financial</Option>
                            <Option value="operational">Operational</Option>
                            <Option value="customer">Customer</Option>
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Button type="primary" icon={<BarChartOutlined />} onClick={exportReport}>
                            Export Report
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Key Metrics */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} lg={6}>
                    <Card className="card-compact">
                        <Statistic
                            title="Total Users"
                            value={stats.totalUsers}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                            suffix={<ArrowUpOutlined style={{ color: '#3f8600' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="card-compact">
                        <Statistic
                            title="Active Vendors"
                            value={stats.totalVendors}
                            prefix={<ShopOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="card-compact">
                        <Statistic
                            title="Total Bookings"
                            value={stats.totalBookings}
                            prefix={<ShoppingCartOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Total Revenue"
                            value={formatCurrency(stats.totalRevenue)}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#f5222d' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Performance Metrics */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} lg={8}>
                    <Card title="Monthly Growth" className="card-compact">
                        <div className="text-center">
                            <Progress
                                type="circle"
                                percent={stats.monthlyGrowth}
                                format={() => `${stats.monthlyGrowth}%`}
                                strokeColor="#52c41a"
                            />
                            <Text type="secondary" className="block mt-2">
                                User growth this month
                            </Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card title="Conversion Rate" className="card-compact">
                        <div className="text-center">
                            <Progress
                                type="circle"
                                percent={stats.conversionRate}
                                format={() => `${stats.conversionRate}%`}
                                strokeColor="#1890ff"
                            />
                            <Text type="secondary" className="block mt-2">
                                Visitor to booking conversion
                            </Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card title="Average Order Value" className="card-compact">
                        <div className="text-center">
                            <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#722ed1' }}>
                                {formatCurrency(stats.averageOrderValue)}
                            </Text>
                            <Text type="secondary" className="block mt-2">
                                Per booking
                            </Text>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Detailed Reports */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card title="Top Performing Vendors" className="card-compact">
                        <Table
                            columns={vendorColumns}
                            dataSource={stats.topVendors}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Recent Bookings" className="card-compact">
                        <Table
                            columns={bookingColumns}
                            dataSource={stats.recentBookings}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Additional Metrics */}
            <Row gutter={[16, 16]} className="mt-6">
                <Col xs={24} lg={8}>
                    <Card title="Customer Retention" className="card-compact">
                        <div className="text-center">
                            <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                {stats.repeatCustomers}
                            </Text>
                            <Text type="secondary" className="block">
                                Repeat customers
                            </Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card title="Revenue Growth" className="card-compact">
                        <div className="text-center">
                            <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                +15.2%
                            </Text>
                            <Text type="secondary" className="block">
                                vs last month
                            </Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card title="Active Bookings" className="card-compact">
                        <div className="text-center">
                            <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                23
                            </Text>
                            <Text type="secondary" className="block">
                                In progress
                            </Text>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Reports;